import { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { IconButton } from './ui/IconButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from './ui/field';
import { EntityType } from '../services/direct';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useRefundDetails } from '../hooks/useRefundDetails';

const SelectEntityTypeDialog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { entityType: urlEntityType } = useSearch({ strict: false });
  const { data } = useRefundDetails();
  const [open, setOpen] = useState(false);
  const [entityType, setEntityType] = useState<EntityType>(
    urlEntityType ?? EntityType.INDIVIDUAL,
  );

  const isCustomPaymentTypesEnabled =
    data?.refundData?.isCustomPaymentTypesEnabled;
  const isUnknownEntityType =
    data?.refundData?.entity_type === EntityType.UNKNOWN;
  const shouldShow = isCustomPaymentTypesEnabled && isUnknownEntityType;

  useEffect(() => {
    if (shouldShow && !urlEntityType) {
      setOpen(true);
    }
  }, [urlEntityType, shouldShow]);

  const handleConfirm = () => {
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, entityType }),
    });
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !urlEntityType) {
      return;
    }
    setOpen(isOpen);
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className='sm:max-w-md'
        onPointerDownOutside={(e) => !urlEntityType && e.preventDefault()}
        onEscapeKeyDown={(e) => !urlEntityType && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('Select Entity Type')}</DialogTitle>
          <DialogDescription>
            {t(
              'Please select the type of entity that will be receiving the refund.',
            )}
          </DialogDescription>
        </DialogHeader>
        <RadioGroup
          value={entityType}
          onValueChange={(value) => setEntityType(value as EntityType)}
          className='gap-3'
        >
          <FieldLabel htmlFor='individual'>
            <Field orientation='horizontal'>
              <FieldContent>
                <FieldTitle>{t('Individual')}</FieldTitle>
                <FieldDescription>
                  {t('For personal accounts and individual recipients.')}
                </FieldDescription>
              </FieldContent>
              <RadioGroupItem value={EntityType.INDIVIDUAL} id='individual' />
            </Field>
          </FieldLabel>
          <FieldLabel htmlFor='business'>
            <Field orientation='horizontal'>
              <FieldContent>
                <FieldTitle>{t('Business')}</FieldTitle>
                <FieldDescription>
                  {t('For business accounts and corporate entities.')}
                </FieldDescription>
              </FieldContent>
              <RadioGroupItem value={EntityType.BUSINESS} id='business' />
            </Field>
          </FieldLabel>
        </RadioGroup>
        <DialogFooter>
          <IconButton
            type='button'
            variant='success'
            Icon={Check}
            onClick={handleConfirm}
          >
            {t('Confirm')}
          </IconButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelectEntityTypeDialog;
